import { font } from '@styles/fonts';
import { formatDate } from '@components/imageHandling/common';
import { isAndroid, isWeb, localTime, navigationRef } from '@helpers';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { addOrUpdateImageDocument } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { uploadImageDocument } from '@reducers/v2/imageHandlingV2/thunks';
import { ActionMenuConfigItem } from '@interfaces/base';
import { ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';

interface UseManageChooseCropModeModalParams {
  pendingCropData: any;
  processCropData: (cropData: any) => void;
  setPendingCropData: (cropData: any) => void;
  closeCrop: () => void;
  image: ImageDocumentStoreItem;
  setShowChooseCropModeModalOpen: (state: boolean) => void;
}

export const useManageChooseCropModeModal = ({
  pendingCropData,
  processCropData,
  setPendingCropData,
  closeCrop,
  image,
  setShowChooseCropModeModalOpen,
}: UseManageChooseCropModeModalParams) => {
  const color = 'Label/Light/Primary';

  const dispatch = useAppDispatch();

  const onCloseShowChooseCropModeModalOpen = () => {
    setShowChooseCropModeModalOpen(false);
    setPendingCropData(null);
  };

  const onSaveImageAsCropOfImage = () => {
    if (pendingCropData) {
      processCropData(pendingCropData);
      onCloseShowChooseCropModeModalOpen();
    }
  };

  const onSaveImageAsNewImage = async () => {
    if (pendingCropData && image) {
      const { width, height } = pendingCropData;

      // Close modal and cropper first
      onCloseShowChooseCropModeModalOpen();
      closeCrop();

      // Use default crop settings for new image (full image, no crop)
      const imageCropSettings = {
        upperLeftXPosition: 0,
        lowerRightXPosition: 1,
        upperLeftYPosition: 0,
        lowerRightYPosition: 1,
        pageNumber: 1,
        rotationAngle: 0,
      };

      // Prepare the photo data with unique filename
      const originalFileName = image.fileName || 'cropped_image.jpg';
      const fileNameParts = originalFileName.split('.');
      fileNameParts.pop(); // Remove original extension
      const baseName = fileNameParts.join('.');
      // Always use .jpg extension for cropped images, regardless of original file type
      const fileNamePrefix = 'Crop of';
      const uniqueFileName = `${baseName.startsWith(fileNamePrefix) ? baseName : fileNamePrefix + ' ' + baseName}.jpg`;

      // Get the image data - could be base64 or file URI
      // Android returns base64 data, iOS returns file URI, web returns data URI
      const imageData = pendingCropData.croppedImageUri || pendingCropData.uri || pendingCropData.base64;

      let photo: any;

      // Check if imageData is a file URI or base64/data URI
      const isFileUri = imageData && imageData.startsWith('file://');
      const isDataUri = imageData && imageData.startsWith('data:');

      if (isWeb) {
        // For web, create a File object from data URI
        const base64Response = await fetch(imageData);
        const blob = await base64Response.blob();
        const file = new File([blob], uniqueFileName, { type: blob.type || 'image/jpeg' });

        photo = {
          file: file,
          name: uniqueFileName,
          type: blob.type || 'image/jpeg',
          width: width,
          height: height,
        };
      } else if (isFileUri) {
        // For iOS (file URI), pass the file URI directly
        photo = {
          uri: imageData,
          name: uniqueFileName,
          type: 'image/jpeg',
          width: width,
          height: height,
        };
      } else {
        // For Android (base64 string)
        // Remove data URI prefix if present to get pure base64
        const pureBase64 = isDataUri ? imageData.replace(/^data:image\/[a-z]+;base64,/, '') : imageData;

        // Create photo object with base64 property for Android
        photo = {
          base64: pureBase64,
          name: uniqueFileName,
          type: 'image/jpeg',
          width: width,
          height: height,
        };
      }

      // Get links from the current image and convert to tags format
      const links = image.links || [];
      const tags = links.map((link) => ({
        scopeId: link.scopeId,
        scopeReferenceId: link.scopeReferenceId,
        scopeName: link.scopeName,
        scopeReferenceName: link.scopeReferenceName,
        isFavorite: link.isFavorite,
      }));

      // Generate a temporary ID for the new image
      const tempID = Math.random().toString(36).substring(7);

      // Prepare image contexts - use current context if available
      const imageContexts = [...tags.map((t) => ({ scopeId: t.scopeId, scopeReferenceId: t.scopeReferenceId })), { documentId: tempID }];

      // Add the new image to store before uploading
      const newImage = {
        id: tempID,
        documentId: tempID,
        base64: imageData,
        url: imageData,
        urlSmall: imageData,
        originUrl: imageData,
        width: width,
        height: height,
        widthOriginal: width,
        heightOriginal: height,
        isDocument: false,
        pageCount: 1,
        pageNumber: 1,
        fileName: uniqueFileName,
        customRotate: { 1: 0 },
        modifiedOn: formatDate(localTime().format('YYYY-MM-DD HH:mm')),
        isProcessing: true,
        tags: tags?.map((el: any) => ({
          ...el,
          key: el.scopeId,
          value: el.scopeReferenceId,
          name: el.scopeReferenceName,
          id: Math.random().toString(36).substring(7),
          selected: true,
          isFavorite: el.isFavorite,
        })),
        documentType: image.documentType,
        documentTag: image.documentTag,
      } as unknown as ImageDocumentStoreItem;

      // Add image to all contexts
      imageContexts.forEach((context) => {
        dispatch(addOrUpdateImageDocument({ imageContext: context, document: newImage, saveToEnd: false }));
      });

      navigationRef?.current?.setParams({ documentId: tempID });

      // Upload the new image document
      dispatch(
        uploadImageDocument({
          photo,
          tags,
          documentType: image.documentTag,
          imageCropSettings,
          tempID,
          imageContexts,
          isSaveCropImageAsNewImageFlow: true,
        })
      );
    }
  };

  const config: ActionMenuConfigItem[] = [
    {
      lable: {
        color,
        name: 'Save Image',
        font: font.Body,
      },
      icon: {
        name: 'fileWithCheckMark',
        width: 17,
        height: 19,
        color,
      },
      onPress: () => onSaveImageAsCropOfImage(),
    },
    {
      lable: {
        color,
        name: 'Save as New Image',
        font: font.Body,
      },
      icon: {
        name: 'fileWithPlusMark',
        width: 17,
        height: 19,
        color,
      },
      onPress: () => onSaveImageAsNewImage(),
    },
  ];

  return {
    config,
    onCloseShowChooseCropModeModalOpen,
  };
};
